import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { Form, Modal, message } from "antd";
import React, { useState } from "react";
import PrintPurchasingLabelForm, { PrintPurchasingLabelFormValues } from "./PrintPurchasingLabelForm";
import OrderItemLabelPrinter from "@/services/print/orderlabel/OrderItemLabelCloudPrinter";
import { PurchasingLabelTemplateDTO } from "@/dto/template/PurchasingLabelTemplateDTO";
import PurchasingLabelCainiaoPrinter, {
    GeneratePurchasingLabelPrintDataStatus,
} from "@/services/print/purchasingLabel/PurchasingLabelCainiaoPrinter";
import { useGlobalStore } from "@/store/hooks";
import { uuid } from "@/utils";
import { markPrintSyncPending } from "@/services/api/moreFeatures/purchasingLabel/scanPurchasingLabel";

interface PrintPurchasingLabelModalProps extends InjectBaseModalProps {
    distributedId: number;

    onPrintDone?: () => void;
}

const PrintPurchasingLabelModal: React.FC<PrintPurchasingLabelModalProps> = function (props) {
    const { onCancel, open, distributedId, onPrintDone } = props;
    const [form] = Form.useForm<PrintPurchasingLabelFormValues>();
    const [purchasingLabelTemplates, setPurchasingLabelTemplates] = useState<PurchasingLabelTemplateDTO[]>();
    const [loading, setLoading] = useState(false);
    const globalStore = useGlobalStore();

    async function handlePrint() {
        if (!OrderItemLabelPrinter.isReady()) {
            OrderItemLabelPrinter.openDownloadPluginDialog();
            return;
        }

        const values = await form.validateFields();
        const { templateId, printer } = values;

        const template = purchasingLabelTemplates.find((it) => it.id === templateId);

        if (!template) {
            Modal.error({
                title: "打印失败",
                content: "无法找到标签模板",
            });
            return;
        }

        const purchasingLabelCainiaoPrinter = new PurchasingLabelCainiaoPrinter(globalStore);

        let current = 0;
        let totalCount = 0;
        let destroyFunc;
        const key = uuid();

        setLoading(true);

        const purchasingLabels = await purchasingLabelCainiaoPrinter.generatePrintData({
            distributedId,
            onProcess({ status, total, errorMsg, data }) {
                if (!totalCount) {
                    totalCount = total;
                }

                if (status === GeneratePurchasingLabelPrintDataStatus.ERROR) {
                    Modal.error({ title: "获取拿货标签数据失败", content: errorMsg });
                } else if (status === GeneratePurchasingLabelPrintDataStatus.RUNNING) {
                    current += data.length;

                    destroyFunc = message.loading({
                        content: `已获取${current}/${totalCount}个拿货标签打印数据`,
                        key,
                        duration: 0,
                    });
                } else {
                    message.success({ content: "已获取到全部拿货标签打印数据" });
                    message.success("已成功发送到打印机");
                }
            },
        });

        purchasingLabels.forEach((label, index) => {
            label._labelIndex = index + 1;
            label._totalLabelCount = purchasingLabels.length;
        });

        setLoading(false);

        destroyFunc?.();

        if (purchasingLabels.length === totalCount) {
            await markPrintSyncPending(distributedId);

            await purchasingLabelCainiaoPrinter.print(purchasingLabels, template, printer);

            onCancel();

            onPrintDone?.();
        }
    }

    return (
        <Modal
            onCancel={onCancel}
            mask={{ closable: false }}
            open={open}
            title="打印拿货标签"
            okText="打印"
            onOk={handlePrint}
            okButtonProps={{ loading }}
        >
            <PrintPurchasingLabelForm form={form} onPurchasingLabelTemplatesLoad={setPurchasingLabelTemplates} />
        </Modal>
    );
};

export default React.memo(PrintPurchasingLabelModal);
